import { selectLeaseComps } from "@/features/survey/leaseCompsSelectors";
import {
  selectPOICategories,
  selectVisiblePOICountByCategoryId,
} from "@/features/survey/poisSelectors";
import {
  selectCurrentSurvey,
  selectMarketMapKeys,
} from "@/features/survey/surveySelectors";
import {
  useAddSurveyMapKeysMutation,
  useDeleteSurveyMapKeyMutation,
  useGetSurveyMapKeysBySurveyQuery,
  useReorderSurveyMapKeyMutation,
  useUpdateSurveyMapKeyMutation,
} from "@/services/survey-mapKeys";
import { getDuplicateMapKeys, getMapKeys } from "@/shared/utils/map";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { isPOIKey } from "@features/survey/utils/poiUtils";
import Switch from "@components/Switch";
import CategoryPin from "@components/CategoryPin";
import { DndContext } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { EmeraldBanner } from "@emerald-react/banner";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldInput } from "@emerald-react/input";
import { EmeraldSideSheet } from "@emerald-react/side-sheet";
import { useSortableItem, useSortableList } from "@shared/hooks/useSortable";
import Analytics from "@shared/utils/tagManagerAnalytics";
import { MapKey } from "@survey-types/survey.mapkeys.types";
import { SurveyPOICategory } from "@survey-types/survey.poi.types";
import {
  deleteMapKey,
  reorderMapKeys,
  updateToggleLabelMapKey,
} from "@survey/surveySlice";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Property } from "../../types/survey.property.types";

const LEASE_COMP_CATEGORY = "LeaseComp";
const PROPERTY_CATEGORY = "Property";

interface MarketMapKeyProps {
  isOpen: boolean;
  onClose: () => void;
}

interface MapKeyItemProps {
  item: MapKey;
  onLabelBlur: (itemId: string, label: string) => void;
  onToggle: (itemId: string) => void;
  categoryMap: Map<string, SurveyPOICategory>;
  categoryNameMap: Map<string, SurveyPOICategory>;
}

const MapKeyItem = ({
  item,
  onLabelBlur,
  onToggle,
  categoryMap,
  categoryNameMap,
}: MapKeyItemProps) => {
  const { attributes, listeners, setNodeRef, style } = useSortableItem(
    item?.mapKeyId ?? "",
  );
  const [labelValue, setLabelValue] = useState(item.label);

  useEffect(() => {
    setLabelValue(item.label);
  }, [item.label]);

  const poiKey = isPOIKey(item, categoryNameMap);

  let poiCategory = undefined;
  if (poiKey) {
    const linkedId = item.referenceId;
    if (linkedId) {
      poiCategory = categoryMap.get(linkedId);
    }
    if (!poiCategory) {
      poiCategory = categoryNameMap.get(item.label?.trim().toLowerCase());
    }
  }

  const pinType = poiCategory?.pinType || "ICON";
  const iconName = poiCategory?.iconData?.iconName;
  const iconColor = poiCategory?.iconData?.color || item.color;
  const activeLogo = poiCategory?.logoData?.logoLinks?.find(
    (l) => l.isActive !== false,
  );
  const logoUrl = activeLogo?.url;

  return (
    <div
      ref={setNodeRef}
      className="map-key-item flex py-1 gap-3 justify-between"
      style={style}
    >
      <div className="map-key-item-left flex gap-3 w-[85%] text-data items-end">
        <span {...attributes} {...listeners} className="cursor-grab h-6">
          <EmeraldIcon
            icon="drag_indicator"
            className="text-gray-400 hover:text-gray-600 transition-colors ml-1"
          />
        </span>
        {poiKey ? (
          <CategoryPin
            pinType={pinType}
            iconColor={iconColor}
            iconName={iconName}
            logoUrl={logoUrl}
            size={20}
            showStroke={true}
            alt={item.label}
          />
        ) : (
          <div
            className={`shrink-0 text-white flex items-center justify-center text-xs font-semibold ${
              item.category === LEASE_COMP_CATEGORY
                ? "w-4 h-4"
                : "rounded-full w-5 h-5"
            }`}
            style={{
              ...(item.isCurrentLocation
                ? { color: item.color }
                : { backgroundColor: item.color }),
            }}
          >
            {item.isCurrentLocation && (
              <EmeraldIcon icon="stars" type="filled" variant="icon" />
            )}
          </div>
        )}
        <EmeraldInput
          value={labelValue}
          placeholder="Map Key Label"
          className="w-64 emerald-standard"
          inputSize="small"
          outlined={false}
          maxLength={35}
          onChange={(e) => setLabelValue(e.target.value)}
          onFocus={() => {}}
          onBlur={() => onLabelBlur(item?.mapKeyId ?? "", labelValue)}
        />
      </div>
      <div className="map-key-item-right flex">
        <Switch
          checked={item.active}
          size="small"
          onChange={() => onToggle(item?.mapKeyId ?? "")}
        />
      </div>
    </div>
  );
};

const MarketMapKey = ({ isOpen, onClose }: MarketMapKeyProps) => {
  const survey = useSelector(selectCurrentSurvey);
  const mapKeys = useSelector(selectMarketMapKeys);
  const allLeaseComps = useSelector(selectLeaseComps);
  const poiCategories = useSelector(selectPOICategories);
  const visiblePOICountByCategoryId = useAppSelector(
    selectVisiblePOICountByCategoryId,
  );
  const isStyleEditInProgress = useAppSelector((state) => state.survey.isStyleEditInProgress);
  const leaseComps = survey?.isIncludeLeaseComps ? allLeaseComps : [];
  const dispatch = useAppDispatch();
  const [hasExceededMaxKeys, setHasExceededMaxKeys] = useState(false);
  const surveyProperties = survey?.properties;
  const { isLoading } = useGetSurveyMapKeysBySurveyQuery(survey?.id || "", {
    refetchOnMountOrArgChange: false,
  });
  const [addSurveyMapKeys] = useAddSurveyMapKeysMutation();
  const [reorderSurveyMapKey] = useReorderSurveyMapKeyMutation();
  const [updateSurveyMapKey] = useUpdateSurveyMapKeyMutation();
  const [deleteSurveyMapKey] = useDeleteSurveyMapKeyMutation();
  const isSyncingRef = useRef(false);
  const isDedupingRef = useRef(false);
  const syncedPOICategoryIdsRef = useRef<Set<string>>(new Set());
  const lastSyncedSurveyIdRef = useRef<string | undefined>(undefined);

  const categoryMap = useMemo(
    () => new Map(poiCategories.map((cat) => [cat.extId, cat])),
    [poiCategories],
  );

  const categoryNameMap = useMemo(
    () =>
      new Map(
        poiCategories.map((cat) => [cat.categoryName.trim().toLowerCase(), cat]),
      ),
    [poiCategories],
  );

  // Immediately remove obvious duplicates when mapKeys load (especially POI duplicates)
  useEffect(() => {
    if (isDedupingRef.current || !mapKeys || mapKeys.length === 0) return;
    
    const duplicates = getDuplicateMapKeys(mapKeys, categoryNameMap);
    if (duplicates.length === 0) return;

    isDedupingRef.current = true;
    
    // Remove duplicates immediately without waiting for sync
    duplicates.forEach((item) => {
      if (item.mapKeyId) {
        deleteSurveyMapKey(item.mapKeyId);
        dispatch(deleteMapKey(item.mapKeyId));
      }
    });
    
    setTimeout(() => {
      isDedupingRef.current = false;
    }, 500);
  }, [mapKeys, categoryNameMap, dispatch, deleteSurveyMapKey]);

  useEffect(() => {
    const numberOfKeys =
      mapKeys?.filter((item) => item.active && !item?.isShortlisted).length ||
      0;
    setHasExceededMaxKeys(numberOfKeys > 8);
  }, [mapKeys]);

  useEffect(() => {
    // Skip if a sync is already in progress (prevents double-fire from StrictMode / mutation updates)
    // Also skip if style edit is in progress
    if (isSyncingRef.current || isLoading || isStyleEditInProgress) return;

    // Reset the synced POI tracking set when the survey changes
    if (survey?.id !== lastSyncedSurveyIdRef.current) {
      syncedPOICategoryIdsRef.current = new Set();
      lastSyncedSurveyIdRef.current = survey?.id;
    }

    // POI mapkeys are managed exclusively via createPOICategory (POST) and
    // category rename (PATCH). The sync only manages Property and LeaseComp keys.
    const allIncoming: MapKey[] = getMapKeys(
      surveyProperties as Property[],
      leaseComps,
    );

    const makeKey = (item: {
      color: string;
      isCurrentLocation: boolean;
      category?: string;
      referenceId?: string;
    }) =>
      `${item.color.toLowerCase()}|${item.isCurrentLocation}|${item.category ?? PROPERTY_CATEGORY}|${item.referenceId ?? ""}`;

    const existingKeys = new Set(mapKeys.map(makeKey));
    const incomingKeys = new Set(allIncoming.map(makeKey));

    const toAdd = allIncoming
      .filter((item) => !existingKeys.has(makeKey(item)))
      .map((item, i) => ({
        ...item,
        order: mapKeys.length + i,
      }));

    // Self-heal missing POI map-key rows so they show in this edit panel.
    // This keeps existing behavior for Property/LeaseComp sync while backfilling
    // POI mapkeys that may be absent in legacy/inconsistent survey data.
    const existingPOICategoryIds = new Set(
      mapKeys
        .filter((item) => isPOIKey(item, categoryNameMap))
        .map((item) => {
          if (item.referenceId) return item.referenceId;
          // Fallback: resolve category from label (legacy keys without ID fields)
          const category = categoryNameMap.get(item.label?.trim().toLowerCase());
          return category?.extId;
        })
        .filter(Boolean),
    );

    const missingPOIMapKeys: MapKey[] =
      survey?.isIncludePOI === false
        ? []
        : poiCategories
            .filter((cat) => !existingPOICategoryIds.has(cat.extId))
            .filter((cat) => !syncedPOICategoryIdsRef.current.has(cat.extId))
            .filter((cat) => (visiblePOICountByCategoryId[cat.extId] || 0) > 0)
            .map((cat, idx) => {
              return {
                label: cat.categoryName,
                color: cat.iconData?.color || "#a23f97",
                active: true,
                isCurrentLocation: false,
                order: mapKeys.length + toAdd.length + idx,
                category: "POI",
                referenceId: cat.extId,
              };
            });
    // Never auto-delete POI mapkeys — they are managed via category create/update only
    const toDelete = mapKeys.filter(
      (item) =>
        !incomingKeys.has(makeKey(item)) &&
        !item.isShortlisted &&
        !isPOIKey(item, categoryNameMap),
    );

    // Detect and remove duplicate map keys — keep the earliest (lowest order), delete the rest
    // Now includes POI duplicates since getDuplicateMapKeys properly identifies them by poiCategoryId
    // Pass categoryNameMap so legacy POI keys (without explicit IDs) can be resolved
    const duplicatesToRemove = getDuplicateMapKeys(mapKeys, categoryNameMap);

    if (
      !toAdd.length &&
      !missingPOIMapKeys.length &&
      !toDelete.length &&
      !duplicatesToRemove.length
    ) {
      return;
    }

    isSyncingRef.current = true;

    if (toAdd.length || missingPOIMapKeys.length) {
      Analytics.send({
        event: "Add Map Key Entries",
        SurveyID: survey?.id,
        SurveyName: survey?.name,
        MapKeysCount: toAdd.length + missingPOIMapKeys.length,
      });
      missingPOIMapKeys.forEach((key) => {
        if (key.referenceId) syncedPOICategoryIdsRef.current.add(key.referenceId);
      });
      addSurveyMapKeys({
        surveyId: survey?.id || "",
        mapKeys: [...toAdd, ...missingPOIMapKeys],
      });
    }

    toDelete.forEach((item) => {
      if (item.mapKeyId) {
        Analytics.send({
          event: "Delete Map Key Entry",
          SurveyID: survey?.id,
          SurveyName: survey?.name,
        });
        deleteSurveyMapKey(item.mapKeyId);
        dispatch(deleteMapKey(item.mapKeyId));
      }
    });

    duplicatesToRemove.forEach((item) => {
      if (item.mapKeyId) {
        deleteSurveyMapKey(item.mapKeyId);
        dispatch(deleteMapKey(item.mapKeyId));
      }
    });

    // Reset flag after a tick so future surveyProperties changes can trigger a sync
    setTimeout(() => {
      isSyncingRef.current = false;
    }, 500);
  }, [
    survey?.id,
    survey?.isIncludePOI,
    surveyProperties,
    leaseComps,
    isLoading,
    categoryMap,
    categoryNameMap,
    visiblePOICountByCategoryId,
  ]);

  const ShortlistedMapKeyItem = ({ item }: { item: MapKey }) => {
    const poiKey = isPOIKey(item, categoryNameMap);

    let poiCategory = undefined;
    if (poiKey) {
    const linkedId = item.referenceId;
      if (linkedId) {
        poiCategory = categoryMap.get(linkedId);
      }
      if (!poiCategory) {
        poiCategory = categoryNameMap.get(item.label?.trim().toLowerCase());
      }
    }

    const pinType = poiCategory?.pinType || "ICON";
    const iconName = poiCategory?.iconData?.iconName;
    const iconColor = poiCategory?.iconData?.color || item.color;
    const activeLogo = poiCategory?.logoData?.logoLinks?.find(
      (l) => l.isActive !== false,
    );
    const logoUrl = activeLogo?.url;

    return (
      <div className="map-key-item flex py-1 gap-3 justify-between opacity-50 pointer-events-none select-none">
      <div className="map-key-item-left flex gap-3 w-[85%] text-data items-end">
        <span className="h-6 cursor-not-allowed">
          <EmeraldIcon icon="drag_indicator" className="text-gray-400 ml-1" />
        </span>
        {poiKey ? (
          <CategoryPin
            pinType={pinType}
            iconColor={iconColor}
            iconName={iconName}
            logoUrl={logoUrl}
            size={20}
            showStroke={true}
            alt={item.label}
          />
        ) : (
          <div
            className={`shrink-0 w-5 h-5 text-white flex items-center justify-center text-xs font-semibold ${
              item.category === LEASE_COMP_CATEGORY
                ? "rounded-sm"
                : "rounded-full"
            }`}
            style={{
              ...(item.isCurrentLocation
                ? { color: item.color }
                : { backgroundColor: item.color }),
            }}
          >
            {item.isCurrentLocation && (
              <EmeraldIcon icon="stars" type="filled" variant="icon" />
            )}
          </div>
        )}
        <EmeraldInput
          value={item.label}
          placeholder="Map Key Label"
          className="w-64 emerald-standard"
          inputSize="small"
          outlined={false}
          maxLength={35}
          onChange={() => {}}
          onFocus={() => {}}
          onBlur={() => {}}
        />
      </div>
      <div className="map-key-item-right flex">
        <Switch checked={item.active} size="small" onChange={() => {}} />
      </div>
    </div>
    );
  };

  const handleToggle = (itemId: string) => {
    let mapKeyItem = mapKeys?.find((item) => item.mapKeyId === itemId);
    Analytics.send({
      event: "Toggle Map Key Entry",
      SurveyID: survey?.id,
      SurveyName: survey?.name,
      MapKeyId: itemId,
      ToggleMapKeyEntry: !mapKeyItem?.active,
    });
    updateSurveyMapKey({
      mapKeyId: itemId,
      mapKey: { ...mapKeyItem, active: !mapKeyItem?.active },
      surveyId: survey?.id || "",
    });
    dispatch(
      updateToggleLabelMapKey({
        ...mapKeyItem,
        active: !mapKeyItem?.active,
      } as MapKey),
    );
  };

  const handleLabelBlur = (itemId: string, label: string) => {
    const mapKeyItem = mapKeys?.find((item) => item.mapKeyId === itemId);
    if (!mapKeyItem) return;

    Analytics.send({
      event: "Map Key Description Changed",
      SurveyID: survey?.id,
      SurveyName: survey?.name,
      MapKeyId: itemId,
      MapKeyLabel: label,
    });

    // Preserve POI key identification: if this is a POI key and it's missing referenceId,
    // try to resolve it from the label so isPOIKey can still detect it correctly.
    // This prevents the key from being replaced with a fallback POI key when the label is changed.
    let updatedMapKey = { ...mapKeyItem, label } as MapKey;
    if (isPOIKey(mapKeyItem, categoryNameMap)) {
      if (!updatedMapKey.referenceId) {
        const category = categoryNameMap.get(label?.trim().toLowerCase());
        if (category?.extId) {
          updatedMapKey.referenceId = category.extId;
        }
      }
    }

    updateSurveyMapKey({
      mapKeyId: itemId,
      mapKey: { label },
      surveyId: survey?.id || "",
    });
    dispatch(updateToggleLabelMapKey(updatedMapKey));
  };

  const handleReorder = (
    reordered: {
      id: string;
      label: string;
      color: string;
      active: boolean;
      isCurrentLocation: boolean;
      order: number;
    }[],
    oldIndex: number,
    newIndex: number,
  ) => {
    const movedKey = nonShortlistedKeys[oldIndex];
    const targetKey = nonShortlistedKeys[newIndex];
    const fullOldIndex = mapKeys.findIndex(
      (k) => k.mapKeyId === movedKey?.mapKeyId,
    );
    const fullNewIndex = mapKeys.findIndex(
      (k) => k.mapKeyId === targetKey?.mapKeyId,
    );
    reorderSurveyMapKey({
      mapKeyId: reordered[newIndex].id,
      surveyId: survey?.id || "",
      order: newIndex,
    });
    dispatch(
      reorderMapKeys({ oldIndex: fullOldIndex, newIndex: fullNewIndex }),
    );
  };

  const shortlistedKeysCount =
    mapKeys?.filter((item) => item.isShortlisted).length ?? 0;

  const nonShortlistedKeys =
    mapKeys?.filter((item) => !item.isShortlisted) ?? [];
  const shortlistedKeys = mapKeys?.filter((item) => item.isShortlisted) ?? [];

  const eighthActiveItemIndex = (() => {
    let count = 0;
    for (let i = 0; i < nonShortlistedKeys.length; i++) {
      if (nonShortlistedKeys[i].active) count++;
      if (count === 8) return i;
    }
    return -1;
  })();

  const { DndContextProps, SortableContextProps } = useSortableList({
    items:
      nonShortlistedKeys.map((item) => ({
        id: item?.mapKeyId ?? "",
        label: item.label,
        color: item.color,
        active: item.active,
        isCurrentLocation: item.isCurrentLocation,
        order: item.order,
      })) || [],
    onReorder: handleReorder,
    idField: "id",
  });

  return (
    <EmeraldSideSheet
      opened={isOpen}
      title="Map Key"
      position="right"
      closeOnOutsideClick={true}
      hasCloseButton={true}
      closeOnEscape={true}
      onClose={onClose}
      className="map-key-display"
      hasDivider={true}
    >
      <div className="map-key-content p-6">
        <div className="map-key-description text-sm text-data-light mb-3 leading-relaxed">
          Create a custom label for each pin color you have on the Market Map.
        </div>

        <div className="map-key-items space-y-4">
          <DndContext {...DndContextProps}>
            <SortableContext {...SortableContextProps}>
              {nonShortlistedKeys.map((item, index) => (
                <Fragment key={item.mapKeyId}>
                  <MapKeyItem
                    key={item.mapKeyId}
                    item={item}
                    onLabelBlur={handleLabelBlur}
                    onToggle={handleToggle}
                    categoryMap={categoryMap}
                    categoryNameMap={categoryNameMap}
                    />
                  {hasExceededMaxKeys && index === eighthActiveItemIndex && (
                    <>
                      <div
                        key="map-key-divider"
                        className="flex items-center gap-3 my-5 mb-2"
                      >
                        <div className="flex-1 border-t border-gray-600" />
                        <span className="text-xs text-gray-400 tracking-widest uppercase whitespace-nowrap">
                          Not Shown in Map Key
                        </span>
                        <div className="flex-1 border-t border-gray-600" />
                      </div>
                      <EmeraldBanner
                        key="map-key-limit-banner"
                        title={""}
                        actionText={""}
                        variant={"info"}
                        content={
                          "The Map Key can display a maximum of 8 colors. Items below will still appear on the map — drag them above this message or hide one of the above to include in the key."
                        }
                        open={true}
                      />
                    </>
                  )}
                </Fragment>
              ))}
            </SortableContext>
          </DndContext>
          {shortlistedKeysCount > 0 && (
            <>
              <div className="flex items-center gap-3 my-5 mb-2">
                <div className="flex-1 border-t border-gray-600" />
                <span className="text-xs text-gray-400 tracking-widest uppercase whitespace-nowrap">
                  Shortlisted by User
                </span>
                <div className="flex-1 border-t border-gray-600" />
              </div>
              <EmeraldBanner
                title={""}
                actionText={""}
                variant={"info"}
                content={
                  <>
                    Shortlisted properties are now grouped in the Map Key.
                    Previous labels are listed below for reference.
                    <br />
                    They will reappear on the Market Map when unshortlisted.
                  </>
                }
                open={true}
              />
              {shortlistedKeys.map((item) => (
                <ShortlistedMapKeyItem key={item.mapKeyId} item={item} />
              ))}
            </>
          )}
        </div>
      </div>
    </EmeraldSideSheet>
  );
};

export default MarketMapKey;
